import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Popable } from "react-native-popable";
import HiringTemplatesAPI from "../../../../API/HiringTemplates";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isMobileScreen } from "../../../MainViewContainer";
import WorkspaceChip from "../../../Workspaces/Components/WorkspaceChip";

const TOOL_TYPES = [
  { key: "mcp", label: "MCP", flag: "isMCPServer", icon: "mcp" },
  { key: "skill", label: "Skill", flag: "isAgentSkill", icon: "skill" },
  { key: "api", label: "API", flag: null, icon: "api" },
  {
    key: "database",
    label: "Database",
    flag: "isDatabaseScript",
    icon: "data",
  },
  { key: "code", label: "Code", flag: "isCodeTemplate", icon: "code" },
  { key: "html", label: "HTML", flag: "isHtmlFn", icon: "html" },
  { key: "chat", label: "Chat", flag: "isChatFn", icon: "chatIcon" },
];

const getToolType = (tool) => {
  if (tool?.isAgentSkill) return "skill";
  if (tool?.isChatFn) return "chat";
  if (tool?.isHtmlFn) return "html";
  if (tool?.isCodeTemplate) return "code";
  if (tool?.isDatabaseScript) return "database";
  if (tool?.isMCPServer) return "mcp";
  return "api";
};

const getGroupInitials = (groupName) => {
  const cleaned = (groupName || "")
    .replace(/\b(tools?|connectors?|integrations?|pack|suite)\b/gi, " ")
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
  return (initials || (groupName || "?").slice(0, 2)).toUpperCase();
};

const GroupAvatar = ({ groupName, currentTheme, size = 34 }) => {
  const backgroundColor = AppUtilities.stringToHslColor(
    groupName || "tools",
    60
  );
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        backgroundColor,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AppText
        style={{ fontSize: size * 0.38, fontWeight: "bold" }}
        color={currentTheme?.theme?.white}
        preventTranslation
      >
        {getGroupInitials(groupName)}
      </AppText>
    </View>
  );
};

const CreateNewCard = ({ typeInfo, onPress, currentTheme }) => {
  const theme = currentTheme?.theme;
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{ marginRight: 10, marginTop: 10 }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: 140,
          paddingHorizontal: 12,
          paddingVertical: 10,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: theme?.app_outline_color,
          backgroundColor: theme?.softBackgroundColor,
        }}
      >
        <AppIcon
          icon={AppAssets.icons[typeInfo.icon] || AppAssets.icons.tools}
          size={15}
          color={theme?.primary}
        />
        <AppText
          style={{ fontSize: 13, fontWeight: "600", marginLeft: 8 }}
          numberOfLines={1}
        >
          {typeInfo.label}
        </AppText>
      </View>
    </TouchableOpacity>
  );
};

const generateToolChips = (tool, currentTheme) => {
  const chips = [];
  const toolType = getToolType(tool);
  const typeInfo = TOOL_TYPES.find((t) => t.key === toolType);

  chips.push({
    label: typeInfo?.label || "API",
    icon: AppAssets.icons.tools,
    customStyle: {
      backgroundColor: currentTheme?.theme?.primary,
      fontColor: currentTheme?.theme?.white,
    },
  });

  chips.push({
    label: tool?.requiresAuth ? "Auth Required" : "No Auth",
    icon: tool?.requiresAuth ? AppAssets.icons.lock : AppAssets.icons.unlock,
    customStyle: {
      backgroundColor: tool?.requiresAuth
        ? currentTheme?.theme?.orange
        : currentTheme?.theme?.mediumGrey,
      fontColor: currentTheme?.theme?.white,
    },
  });

  return chips;
};

const matchesToolType = (tool, typeKey) => {
  if (!typeKey || typeKey === "all") return true;
  const typeInfo = TOOL_TYPES.find((t) => t.key === typeKey);
  if (!typeInfo) return true;
  if (typeInfo.flag) return tool?.[typeInfo.flag] === true;
  return (
    !tool?.isMCPServer &&
    !tool?.isDatabaseScript &&
    !tool?.isCodeTemplate &&
    !tool?.isHtmlFn &&
    !tool?.isChatFn &&
    !tool?.isAgentSkill
  );
};

const matchesSearch = (tool, groupName, term) => {
  if (!term) return true;
  const needle = term.toLowerCase();
  return (
    tool?.name?.toLowerCase().includes(needle) ||
    tool?.description?.toLowerCase().includes(needle) ||
    (groupName || "")?.toLowerCase().includes(needle)
  );
};

const ToolCard = ({
  tool,
  toolTypeIcon,
  onPress,
  currentTheme,
  isSelected,
  t,
}) => {
  const theme = currentTheme?.theme;
  const chips = generateToolChips(tool, currentTheme);
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={`tool-card-${tool.id}`}
    >
      <View
        style={{
          width: 320,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: isSelected ? theme?.primary : theme?.app_outline_color,
          backgroundColor: theme?.softBackgroundColor,
          padding: 16,
          marginLeft: 20,
          marginBottom: 16,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              backgroundColor: theme?.primary,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppIcon icon={toolTypeIcon} size={16} color={theme?.white} />
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <AppText
              style={{ fontSize: 15, fontWeight: "600" }}
              numberOfLines={1}
              preventTranslation
            >
              {tool?.name}
            </AppText>
            {!!tool?.groupName && (
              <AppText
                style={{ fontSize: 11, fontWeight: "bold", marginTop: 2 }}
                color={theme?.orange}
                numberOfLines={1}
                preventTranslation
              >
                {tool?.groupName}
              </AppText>
            )}
          </View>
          {!!tool?.description && (
            <Popable
              numberOfLines={10}
              backgroundColor={theme?.tooltipBackgroundColor}
              style={{ maxWidth: 260, width: 260, zIndex: 20 }}
              content={tool.description}
              action="hover"
              position="bottom"
            >
              <AppIcon
                icon={AppAssets.icons.info}
                size={14}
                color={theme?.mediumGrey}
              />
            </Popable>
          )}
        </View>
        <AppText
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: theme?.darkGrey,
            marginTop: 10,
            minHeight: 34,
          }}
          numberOfLines={2}
          preventTranslation
        >
          {tool?.description || t("settings.list.noDescriptionProvided")}
        </AppText>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 6,
            marginTop: 12,
          }}
        >
          {chips.map((chip, index) => (
            <WorkspaceChip
              key={index}
              label={chip.label}
              leftIcon={chip.icon}
              {...(chip.customStyle && {
                backgroundColor: chip.customStyle.backgroundColor,
                fontColor: chip.customStyle.fontColor,
              })}
            />
          ))}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const ToolPreviewPanel = ({ tool, currentTheme, t, onHire }) => {
  const theme = currentTheme?.theme;
  const chips = tool ? generateToolChips(tool, currentTheme) : [];
  const typeInfo = tool
    ? TOOL_TYPES.find((x) => x.key === getToolType(tool))
    : null;

  return (
    <View
      style={{
        width: 360,
        borderLeftWidth: 1,
        borderLeftColor: theme?.lightGrey,
        flexDirection: "column",
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {!tool ? (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: 30,
          }}
        >
          <AppPlaceholderView
            wrapperStyle={{
              width: "80%",
              height: "100%",
              marginVertical: "auto",
              marginHorizontal: "auto",
              alignItems: "center",
            }}
            mainCaption={t("settings.qa.noToolSelected", "No tool selected")}
            subCaption={t(
              "settings.qa.noToolSelectedDesc",
              "Select a connector from the list to preview its details and import options"
            )}
            size={30}
            icon={AppAssets.icons.tools}
          />
        </View>
      ) : (
        <>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: 24, paddingBottom: 12 }}
            showsVerticalScrollIndicator
          >
            <AppText
              style={{
                fontSize: 11,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: theme?.mediumGrey,
              }}
            >
              {t("settings.qa.preview")}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <GroupAvatar
                groupName={tool?.groupName}
                currentTheme={currentTheme}
              />
              <AppText
                style={{
                  fontSize: 20,
                  fontWeight: "600",
                  marginLeft: 12,
                  flex: 1,
                }}
                preventTranslation
              >
                {tool?.name}
              </AppText>
            </View>
            {!!tool?.groupName && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <AppText
                  style={{
                    fontSize: 11,
                    fontWeight: "bold",
                    color: theme?.orange,
                  }}
                  preventTranslation
                >
                  {tool?.groupName}
                </AppText>
              </View>
            )}
            {!!tool?.description && (
              <AppText
                style={{
                  fontSize: 13,
                  lineHeight: 20,
                  color: theme?.darkGrey,
                  marginTop: 16,
                }}
                preventTranslation
              >
                {tool?.description}
              </AppText>
            )}
            {chips?.length > 0 && (
              <>
                <AppText
                  style={{
                    fontSize: 11,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: theme?.mediumGrey,
                    marginTop: 24,
                    marginBottom: 10,
                  }}
                >
                  {t("settings.qa.capabilitiesLabel")}
                </AppText>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  {chips.map((chip, index) => (
                    <WorkspaceChip
                      key={index}
                      label={chip.label}
                      leftIcon={chip.icon}
                      {...(chip.customStyle && {
                        backgroundColor: chip.customStyle.backgroundColor,
                        fontColor: chip.customStyle.fontColor,
                      })}
                    />
                  ))}
                </View>
              </>
            )}
            <View
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTopWidth: 1,
                borderTopColor: theme?.lightGrey,
              }}
            >
              <AppText
                style={{
                  fontSize: 11,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: theme?.mediumGrey,
                  marginBottom: 10,
                }}
              >
                {t("settings.qa.setupRequirements")}
              </AppText>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <AppIcon
                  icon={AppAssets.icons.info}
                  size={12}
                  color={tool?.requiresAuth ? theme?.orange : theme?.primary}
                />
                <AppText
                  style={{
                    fontSize: 12,
                    color: tool?.requiresAuth ? theme?.orange : theme?.primary,
                    marginLeft: 6,
                  }}
                >
                  {tool?.requiresAuth
                    ? t("settings.qa.apiKeysRequired")
                    : t(
                        "settings.qa.noAuthRequired",
                        "No authorisation required"
                      )}
                </AppText>
              </View>
              {!!typeInfo && (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <AppIcon
                    icon={
                      AppAssets.icons[typeInfo.icon] || AppAssets.icons.tools
                    }
                    size={12}
                    color={theme?.primary}
                  />
                  <AppText
                    style={{
                      fontSize: 12,
                      color: theme?.primary,
                      marginLeft: 6,
                    }}
                  >
                    {typeInfo.label}
                  </AppText>
                </View>
              )}
            </View>
          </ScrollView>
          <View
            style={{
              padding: 20,
              borderTopWidth: 1,
              borderTopColor: theme?.lightGrey,
            }}
          >
            <ToothFairyButton
              title={t("settings.qa.importThisTool", "Import this tool")}
              onPress={() => onHire(tool)}
              wrapperStyle={{ width: "100%" }}
              style={{ marginHorizontal: 0, height: 38 }}
            />
            {!!tool?.requiresAuth && !tool?.isAgentSkill && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 12,
                  flexWrap: "wrap",
                }}
              >
                <AppIcon
                  icon={AppAssets.icons.wizard}
                  size={12}
                  color={theme?.mediumGrey}
                />
                <AppText
                  wrapperStyle={{ flexShrink: 1, marginLeft: 6 }}
                  style={{
                    fontSize: 11,
                    color: theme?.mediumGrey,
                    textAlign: "center",
                    flexShrink: 1,
                  }}
                >
                  {t(
                    "settings.qa.includesGuidedSetupImport",
                    "Guided setup included — configure connections step by step after import"
                  )}
                </AppText>
              </View>
            )}
          </View>
        </>
      )}
    </View>
  );
};

const FunctionPreSelection = ({
  onNewToolSelect,
  onTemplateToolSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { state, getActiveWorkspace } = useWorkspaces();

  const [flatTools, setFlatTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchName, setSearchName] = useState("");
  const [activeType, setActiveType] = useState("all");
  const [selectedToolId, setSelectedToolId] = useState(null);
  const showPreview = width >= 1100;
  const isFirstRenderRef = useRef(true);

  const isAuGovUser = useMemo(() => {
    const rootUserId = getActiveWorkspace()?.userID;
    const rootUser = state?.workspaceUsers?.find((u) => u?.id === rootUserId);
    return rootUser?.user_email?.toLowerCase()?.endsWith(".gov.au") || false;
  }, [state?.workspaceUsers, getActiveWorkspace()?.userID]);

  const filteredFlatTools = useMemo(() => {
    if (isAuGovUser) return flatTools;
    return flatTools.filter((tool) => !tool?.isAuGov);
  }, [flatTools, isAuGovUser]);

  useEffect(() => {
    loadToolsFromTemplates();
  }, []);

  const loadToolsFromTemplates = async () => {
    try {
      setIsLoading(true);
      const groups = await HiringTemplatesAPI.fetchHireableToolsFromTemplates();

      const tools = [];
      Object.entries(groups).forEach(([groupName, groupData]) => {
        (groupData.tools || []).forEach((tool) => {
          tools.push({
            ...tool,
            groupName,
            fields: groupData.fields,
          });
        });
      });

      setFlatTools(tools);
    } catch (error) {
      console.error("Failed to load tools from templates:", error);
      setFlatTools([]);
    } finally {
      setIsLoading(false);
    }
  };

  const visibleTools = useMemo(() => {
    return filteredFlatTools.filter(
      (tool) =>
        matchesToolType(tool, activeType) &&
        matchesSearch(tool, tool?.groupName, searchName)
    );
  }, [filteredFlatTools, activeType, searchName]);

  const groupedTools = useMemo(() => {
    const groups = {};
    visibleTools.forEach((tool) => {
      const typeKey = getToolType(tool);
      if (!groups[typeKey]) groups[typeKey] = [];
      groups[typeKey].push(tool);
    });
    return TOOL_TYPES.filter((type) => groups[type.key]?.length > 0).map(
      (type) => [type, groups[type.key]]
    );
  }, [visibleTools]);

  const selectedTool = useMemo(
    () => visibleTools?.find((tool) => tool?.id === selectedToolId) || null,
    [visibleTools, selectedToolId]
  );

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (
      selectedToolId &&
      !visibleTools.some((tool) => tool?.id === selectedToolId)
    ) {
      setSelectedToolId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTools]);

  const handleCardPress = (tool) => {
    if (showPreview) {
      setSelectedToolId(tool?.id);
    } else {
      onTemplateToolSelect(tool);
    }
  };

  return (
    <ToothFairyModal
      modalStyle={{
        minHeight: 500,
        maxWidth: 1600,
        width: isMobileScreen() ? "99%" : "95%",
        height: "94%",
        marginHorizontal: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        margin: "auto",
      }}
      key="tool-preselection"
      isModalOpen={true}
      setIsModalOpen={onClose}
      modalTitle={t("settings.qa.createTool")}
      closeModal={onClose}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder caption="messages.loadingTools" />
      ) : (
        <View
          style={{
            flexDirection: "row",
            flex: 1,
            minHeight: 0,
          }}
        >
          <View
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              flexDirection: "column",
            }}
          >
            <View
              style={{
                padding: 24,
                paddingBottom: 16,
                borderBottomWidth: 1,
                borderBottomColor: currentTheme?.theme?.lightGrey,
              }}
            >
              <AppText
                style={{
                  fontSize: 22,
                  fontWeight: "600",
                  color: currentTheme?.theme?.font_color,
                }}
              >
                {t("settings.qa.createTool")}
              </AppText>
              <AppText
                style={{
                  fontSize: 13,
                  color: currentTheme?.theme?.darkGrey,
                  marginTop: 4,
                  lineHeight: 19,
                }}
              >
                {t(
                  "settings.qa.createToolSub",
                  "Create a custom tool or import a pre-built connector"
                )}
              </AppText>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginTop: 16,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <AppTextInput
                    value={searchName}
                    onChangeText={setSearchName}
                    style={{
                      marginLeft: 5,
                      minWidth: 320,
                    }}
                    placeholder={t("settings.qa.searchByName")}
                    leftIcon={AppAssets.icons.search}
                    iconColor={currentTheme?.theme?.grey}
                  />
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <AppDropDown
                    dropDownStyle={{
                      width: 200,
                      marginLeft: 5,
                      borderColor: currentTheme?.theme?.border_color,
                    }}
                    isElevated={false}
                    dropdownIconColor="transparent"
                    translateLabels={false}
                    items={[
                      {
                        name: t("settings.qa.allTypes", "All types"),
                        id: "all",
                      },
                      ...TOOL_TYPES.map((type) => ({
                        name: type.label,
                        id: type.key,
                      })),
                    ]}
                    propertyLabel="name"
                    propertyValue="id"
                    selectedValue={activeType}
                    onValueChange={(itemValue) => setActiveType(itemValue)}
                  />
                </View>
              </View>
              <AppText
                style={{
                  fontSize: 11,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: currentTheme?.theme?.mediumGrey,
                  marginTop: 20,
                }}
              >
                {t("settings.qa.createNew", "Create new")}
              </AppText>
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  marginRight: -10,
                }}
              >
                {TOOL_TYPES.map((typeInfo) => (
                  <CreateNewCard
                    key={typeInfo.key}
                    typeInfo={typeInfo}
                    currentTheme={currentTheme}
                    onPress={() => onNewToolSelect(typeInfo.key)}
                  />
                ))}
              </View>
            </View>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: 24 }}
              showsVerticalScrollIndicator
            >
              {groupedTools.length > 0 ? (
                groupedTools.map(([typeInfo, tools]) => (
                  <View key={typeInfo.key} style={{ marginTop: 20 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginLeft: 20,
                        marginBottom: 12,
                      }}
                    >
                      <View
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 7,
                          backgroundColor: currentTheme?.theme?.primary,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AppIcon
                          icon={
                            AppAssets.icons[typeInfo.icon] ||
                            AppAssets.icons.tools
                          }
                          size={13}
                          color={currentTheme?.theme?.white}
                        />
                      </View>
                      <AppText
                        style={{
                          fontSize: 13,
                          fontWeight: "bold",
                          marginLeft: 10,
                        }}
                        preventTranslation
                      >
                        {typeInfo.label}
                      </AppText>
                      <AppText
                        style={{ fontSize: 12, marginLeft: 8 }}
                        color={currentTheme?.theme?.mediumGrey}
                      >
                        {tools.length}
                      </AppText>
                    </View>
                    <View
                      style={{
                        flexWrap: "wrap",
                        justifyContent: "flex-start",
                        flexDirection: "row",
                      }}
                    >
                      {tools.map((tool) => (
                        <ToolCard
                          key={tool.id}
                          tool={tool}
                          t={t}
                          toolTypeIcon={
                            AppAssets.icons[
                              TOOL_TYPES.find(
                                (x) => x.key === getToolType(tool)
                              )?.icon
                            ] || AppAssets.icons.tools
                          }
                          currentTheme={currentTheme}
                          isSelected={
                            showPreview && selectedToolId === tool?.id
                          }
                          onPress={() => handleCardPress(tool)}
                        />
                      ))}
                    </View>
                  </View>
                ))
              ) : (
                <View
                  style={{
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 40,
                  }}
                >
                  <AppPlaceholderView
                    wrapperStyle={{
                      width: "80%",
                      height: 150,
                      alignItems: "center",
                    }}
                    mainCaption={t(
                      "settings.qa.noToolsMatchSearch",
                      "No connectors match your search"
                    )}
                    subCaption={t(
                      "settings.qa.noToolsMatchSearchSub",
                      "Tool hiring from templates is currently in preview. You can create your own tool from scratch above."
                    )}
                    size={40}
                    icon={AppAssets.icons.search}
                  />
                </View>
              )}
            </ScrollView>
          </View>
          {showPreview && (
            <ToolPreviewPanel
              tool={selectedTool}
              currentTheme={currentTheme}
              t={t}
              onHire={onTemplateToolSelect}
            />
          )}
        </View>
      )}
    </ToothFairyModal>
  );
};

export default FunctionPreSelection;
